import { Link } from 'react-router-dom';
import { CONTROLADOR } from '@/config/controlador';

/**
 * Página PÚBLICA de exclusão de conta — exigência da Play Store (URL acessível sem
 * login, que diga como pedir a exclusão e o que é apagado e o que fica).
 *
 * Não é o caminho da exclusão: esse mora em `/conta` (`ExcluirConta.tsx`). Esta
 * página descreve aquele caminho para quem está FORA do app — o revisor da loja,
 * ou quem não consegue mais entrar. Cada afirmação sobre o que some e o que fica
 * tem o seu `if` em `userService.excluirPropriaConta`; as recusas da última seção
 * são as quatro "Recusa" daquela função. Mudou lá, muda aqui. A exceção é "As contas
 * Google vinculadas": não tem `if` — somem pelo `onDelete: Cascade` de `ContaGoogle` em
 * `schema.prisma`, quando `apagarContaAnonimizando` apaga a linha do usuário.
 */
const ExcluirContaPublicaPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <h1 className="font-titulo text-3xl font-bold text-tinta mb-2">Excluir sua conta do Portal Simulados</h1>
        <p className="text-sm text-gray-500 mb-8">
          Portal Simulados — operado por {CONTROLADOR.nome}
        </p>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 space-y-6 text-gray-600 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">Pelo aplicativo ou pelo site</h2>
            <ol className="list-decimal list-inside space-y-1">
              <li>Entre com sua conta.</li>
              <li>Abra o menu e toque em <strong>Minha conta</strong>.</li>
              <li>Toque em <strong>Excluir minha conta</strong>.</li>
              <li>Digite seu e-mail para confirmar.</li>
            </ol>
            <p className="mt-2">A exclusão é imediata e não pode ser desfeita.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">Se você não consegue entrar</h2>
            <p>
              Escreva para{' '}
              <a href={`mailto:${CONTROLADOR.contato}?subject=Exclus%C3%A3o%20de%20conta`} className="text-marca-600 hover:underline">
                {CONTROLADOR.contato}
              </a>{' '}
              a partir do e-mail cadastrado, informando seu nome de usuário. Respondemos em até 15 dias.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">O que é apagado</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Seu nome, e-mail, nome de usuário e senha.</li>
              <li>Seu vínculo com a escola e com as turmas.</li>
              <li>As contas Google vinculadas.</li>
              <li>As imagens das suas folhas de resposta digitalizadas.</li>
              <li>Provas que você começou e não entregou, e convites para aplicação offline.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">O que permanece, sem vínculo com você</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Estudantes:</strong> as respostas às provas que você entregou permanecem, sem vínculo com
                você, nas estatísticas da escola.
              </li>
              <li>
                <strong>Professores:</strong> as questões e os simulados que você criou continuam disponíveis para a
                escola, sem o seu nome — outros professores podem estar usando. As notas já lançadas dos estudantes
                também permanecem, assim como o registro das importações de documento (custo e quantidade de
                questões), sem o seu nome.
              </li>
              <li>Os relatos e os avisos sobre questões que você enviou, sem o seu nome.</li>
              <li>Registros de acesso do servidor, por até 14 dias.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-tinta mb-3">Quando é preciso um passo antes</h2>
            <p>A tela de exclusão avisa e diz o que fazer em cada um destes casos:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Você é a única pessoa da equipe gestora da sua escola.</li>
              <li>Você tem registros de intervenção pedagógica, que não têm cópia em outro lugar.</li>
              <li>Você tem salas de aplicação offline criadas.</li>
            </ul>
          </section>

          <div className="pt-4 border-t flex gap-4 text-sm">
            <Link to="/privacidade" className="text-marca-600 hover:underline">Política de privacidade</Link>
            <Link to="/" className="text-marca-600 hover:underline">Página inicial</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcluirContaPublicaPage;
